import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { api } from '../../services/api';
import { X, GearSix, Printer, Copy, CalendarPlus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PLANNER_DAYS, TIME_SLOTS, LESSON_COLORS, timeToSlotIndex, slotIndexToTime, getSlotCount } from './constants';

// Status flag buttons: X, I, R1, R2, R3
// X = in attesa pagamento, I = iscrizione pagata, R1/R2/R3 = rata 1/2/3 pagata
const STATUS_FLAGS_ORDER = ['x', 'i', 'r1', 'r2', 'r3'];
const STATUS_FLAG_LABELS = { x: 'X', i: 'I', r1: 'R1', r2: 'R2', r3: 'R3' };

export const ScheduleGrid = forwardRef(({
  lessons,
  availability,
  onCellClick,
  onEditLesson,
  onDeleteLesson,
  onDuplicateLesson,
  onGenerateAttendance,
  onAvailabilityClick,
  loadData,
  updateLessonApi,
  onToggleStatusFlag,
  onToggleProva,
  teacherName,
  teacherInstrument,
  hideInternalPrintButton = false,
}, ref) => {
  const [draggingLesson, setDraggingLesson] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [resizingLesson, setResizingLesson] = useState(null);

  // Duration picker
  const [durationPicker, setDurationPicker] = useState(null); // { day, time, x, y }
  const pickerRef = useRef(null);

  // Close duration picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setDurationPicker(null);
      }
    };
    if (durationPicker) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [durationPicker]);

  const handlePrint = useCallback(() => {
    // Build print-friendly HTML table
    const hourSlots = TIME_SLOTS.filter((_, i) => i % 4 === 0);
    // Build a map: day -> sorted lessons
    const dayLessons = {};
    PLANNER_DAYS.forEach((_, di) => {
      dayLessons[di] = lessons.filter(l => l.day === di).sort((a, b) => a.start_time.localeCompare(b.start_time));
    });

    const rows = hourSlots.map(hour => {
      const cells = PLANNER_DAYS.map((_, di) => {
        const cellLessons = lessons.filter(l => {
          const ls = timeToSlotIndex(l.start_time);
          const hs = timeToSlotIndex(hour);
          return l.day === di && ls >= hs && ls < hs + 4;
        }).sort((a, b) => a.start_time.localeCompare(b.start_time));
        return cellLessons.map(l => {
          const active = new Set(l.status_flags || []);
          const flagsHtml = STATUS_FLAGS_ORDER.map(f => {
            const on = active.has(f);
            return `<span style="display:inline-block;padding:0 3px;margin-right:1px;border-radius:2px;font-size:8px;font-weight:800;letter-spacing:0.5px;${on ? 'background:#16A34A;color:#fff;' : 'background:#fff;color:#999;border:1px solid #ccc;'}">${STATUS_FLAG_LABELS[f]}</span>`;
          }).join('');
          return `<div style="margin:1px 0;padding:2px 4px;background:${LESSON_COLORS[getStudentColorIndex(l.student_id)] || '#ddd'};border-radius:3px;font-size:10px;line-height:1.3"><strong>${l.student_name}</strong><br/>${l.start_time}-${l.end_time}<br/><span style="display:inline-flex;gap:1px;margin-top:2px">${flagsHtml}</span></div>`;
        }).join('');
      });
      return `<tr><td style="border:1px solid #ccc;padding:4px;font-weight:600;text-align:right;width:50px;vertical-align:top;font-size:11px">${hour}</td>${cells.map(c => `<td style="border:1px solid #ccc;padding:2px;vertical-align:top;min-width:120px">${c || ''}</td>`).join('')}</tr>`;
    });

    const legendHtml = `
      <div style="margin-top:14px;padding:8px 10px;background:#f4f4f5;border:1px solid #e4e4e7;border-radius:4px;font-size:10px;color:#52525B">
        <strong style="text-transform:uppercase;letter-spacing:1px;color:#2D2A4A;margin-right:8px">Legenda stato:</strong>
        <span style="margin-right:12px"><span style="display:inline-block;padding:1px 5px;background:#16A34A;color:#fff;font-weight:800;border-radius:2px;font-size:9px">X</span> in attesa pagamento</span>
        <span style="margin-right:12px"><span style="display:inline-block;padding:1px 5px;background:#16A34A;color:#fff;font-weight:800;border-radius:2px;font-size:9px">I</span> iscrizione pagata</span>
        <span style="margin-right:12px"><span style="display:inline-block;padding:1px 5px;background:#16A34A;color:#fff;font-weight:800;border-radius:2px;font-size:9px">R1</span> rata 1 pagata</span>
        <span style="margin-right:12px"><span style="display:inline-block;padding:1px 5px;background:#16A34A;color:#fff;font-weight:800;border-radius:2px;font-size:9px">R2</span> rata 2 pagata</span>
        <span><span style="display:inline-block;padding:1px 5px;background:#16A34A;color:#fff;font-weight:800;border-radius:2px;font-size:9px">R3</span> rata 3 pagata</span>
      </div>`;

    const title = teacherName ? `Orario Settimanale - ${teacherName}${teacherInstrument ? ` (${teacherInstrument})` : ''}` : 'Orario Settimanale';
    const html = `<!DOCTYPE html><html><head><title>${title}</title><style>
      body{font-family:'Segoe UI',sans-serif;margin:20px;color:#1a1a1a}
      h1{font-size:18px;margin-bottom:4px}
      p{font-size:12px;color:#666;margin:0 0 12px}
      table{border-collapse:collapse;width:100%}
      th{background:#2D2A4A;color:#fff;padding:8px;text-align:center;font-size:11px;text-transform:uppercase;letter-spacing:1px}
      @media print{body{margin:10px}h1{font-size:16px}}
    </style></head><body>
      <h1>${title}</h1>
      <p>Stampato il ${new Date().toLocaleDateString('it-IT', { day:'2-digit', month:'long', year:'numeric' })}</p>
      <table>
        <thead><tr><th style="width:50px">Ora</th>${PLANNER_DAYS.map(d => `<th>${d}</th>`).join('')}</tr></thead>
        <tbody>${rows.join('')}</tbody>
      </table>
      ${legendHtml}
    </body></html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.print(); };
  }, [lessons, teacherName, teacherInstrument]);

  useImperativeHandle(ref, () => ({ print: handlePrint }), [handlePrint]);

  const getStudentColorIndex = (studentId) => {
    const studentIds = [...new Set(lessons.map(l => l.student_id))];
    return studentIds.indexOf(studentId) % LESSON_COLORS.length;
  };

  const handleDragStart = (e, lesson) => {
    setIsDragging(true);
    setDraggingLesson(lesson);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', lesson.id);
    const dragImage = e.target.cloneNode(true);
    dragImage.style.opacity = '0.8';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 50, 20);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggingLesson(null);
    setDragOverCell(null);
  };

  const handleDragOver = (e, day, time) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell({ day, time });
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverCell(null);
  };

  const handleDrop = async (e, day, time) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCell(null);
    if (!draggingLesson) return;

    if (draggingLesson.day === day && draggingLesson.start_time === time) {
      setDraggingLesson(null);
      return;
    }

    const duration = getSlotCount(draggingLesson.start_time, draggingLesson.end_time);
    const newStartIndex = timeToSlotIndex(time);
    const newEndIndex = newStartIndex + duration;

    if (newEndIndex > TIME_SLOTS.length) {
      toast.error('La lezione non può finire dopo le 20:00');
      setDraggingLesson(null);
      return;
    }

    const newEndTime = newEndIndex >= TIME_SLOTS.length ? '20:00' : TIME_SLOTS[newEndIndex];

    try {
      const updateFn = updateLessonApi || ((id, data) => api.updateLesson(id, data));
      await updateFn(draggingLesson.id, { day, start_time: time, end_time: newEndTime });
      toast.success('Lezione spostata');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nello spostamento');
    }
    setDraggingLesson(null);
  };

  const handleResizeStart = (e, lesson) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingLesson(lesson);

    const onMove = (ev) => {
      const deltaY = ev.clientY - e.clientY;
      const slotDelta = Math.round(deltaY / 28);
      const origSlots = getSlotCount(lesson.start_time, lesson.end_time);
      const newSlots = Math.max(1, Math.min(4, origSlots + slotDelta));
      const el = document.querySelector(`[data-testid="lesson-${lesson.id}"]`);
      if (el) el.style.height = `${newSlots * 28 - 4}px`;
    };

    const onUp = async (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);

      const deltaY = ev.clientY - e.clientY;
      const slotDelta = Math.round(deltaY / 28);
      const origSlots = getSlotCount(lesson.start_time, lesson.end_time);
      const newSlots = Math.max(1, Math.min(4, origSlots + slotDelta));

      if (newSlots !== origSlots) {
        const startIndex = timeToSlotIndex(lesson.start_time);
        const newEndIndex = startIndex + newSlots;
        const newEndTime = newEndIndex >= TIME_SLOTS.length ? '20:00' : TIME_SLOTS[newEndIndex];
        try {
          const updateFn = updateLessonApi || ((id, data) => api.updateLesson(id, data));
          await updateFn(lesson.id, { end_time: newEndTime });
          toast.success(`Lezione: ${newSlots * 15} minuti`);
          loadData();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Errore nel ridimensionamento');
          loadData();
        }
      }
      setResizingLesson(null);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleCellClickInternal = (e, dayIndex, time) => {
    if (availability[dayIndex]?.includes(time)) return;
    const existingLesson = lessons.find(l => l.day === dayIndex && l.start_time === time);
    if (existingLesson) {
      onEditLesson(existingLesson);
      return;
    }
    // Only show duration picker if onCellClick is provided (admin only)
    if (!onCellClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setDurationPicker({ day: dayIndex, time, x: rect.left + rect.width / 2, y: rect.bottom });
  };

  const handleDurationSelect = (slots) => {
    if (!durationPicker) return;
    const { day, time } = durationPicker;
    const startIndex = timeToSlotIndex(time);
    const endIndex = Math.min(startIndex + slots, TIME_SLOTS.length);
    const endTime = endIndex >= TIME_SLOTS.length ? '20:00' : TIME_SLOTS[endIndex];
    setDurationPicker(null);
    onCellClick(day, time, endTime);
  };

  const DURATION_OPTIONS = [
    { slots: 2, label: '30 min' },
    { slots: 3, label: '45 min' },
    { slots: 4, label: '60 min' },
  ];

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden select-none relative">
      {/* Print button (hidden when parent renders its own) */}
      {!hideInternalPrintButton && (
        <div className="flex justify-end p-2 border-b border-[#E4E4E7] bg-[#FAFAFA] no-print">
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#2D2A4A] bg-white border border-[#2D2A4A] rounded-sm hover:bg-[#2D2A4A] hover:text-white transition-colors"
            data-testid="print-schedule-button"
          >
            <Printer size={14} />
            Stampa Orario
          </button>
        </div>
      )}
      {/* Header */}
      <div className="grid border-b-2 border-[#0A0A0A]" style={{ gridTemplateColumns: '50px repeat(5, 1fr)' }}>
        <div className="p-2 text-[10px] font-semibold uppercase tracking-wider text-[#52525B] bg-[#FAFAFA] border-r border-[#E4E4E7]">
          Ora
        </div>
        {PLANNER_DAYS.map((day, index) => (
          <div key={day} className="p-3 text-xs font-semibold uppercase tracking-wider text-center bg-[#FAFAFA] border-r border-[#E4E4E7] last:border-r-0 relative">
            {day}
            <button
              onClick={(e) => { e.stopPropagation(); onAvailabilityClick(index); }}
              className="absolute top-2 right-2 p-1 hover:bg-[#E4E4E7] rounded transition-colors"
              title="Gestisci disponibilità"
            >
              <GearSix size={14} className="text-[#52525B]" />
            </button>
          </div>
        ))}
      </div>

      {/* Time Grid */}
      <div className="relative">
        {TIME_SLOTS.map((time, timeIndex) => (
          <div key={time} className="grid" style={{ gridTemplateColumns: '50px repeat(5, 1fr)', height: '28px' }}>
            <div className={`text-right pr-1 text-[10px] text-[#52525B] border-r border-[#E4E4E7] flex items-center justify-end ${timeIndex % 4 === 0 ? 'font-medium' : ''}`}>
              {time}
            </div>
            {PLANNER_DAYS.map((_, dayIndex) => {
              const isDisabled = availability[dayIndex]?.includes(time);
              const lessonAtCell = lessons.find(l => l.day === dayIndex && l.start_time === time);
              const isOccupied = lessons.some(l =>
                l.day === dayIndex && l.start_time !== time &&
                timeToSlotIndex(l.start_time) < timeToSlotIndex(time) &&
                timeToSlotIndex(l.end_time) > timeToSlotIndex(time)
              );

              return (
                <div
                  key={`${dayIndex}-${time}`}
                  className={`
                    border-r border-b border-[#E4E4E7] last:border-r-0 relative
                    ${isDisabled ? 'bg-red-50 cursor-not-allowed' : 'cursor-pointer hover:bg-blue-50'}
                    ${dragOverCell?.day === dayIndex && dragOverCell?.time === time && !isDisabled ? 'bg-blue-200 ring-2 ring-blue-400 ring-inset' : ''}
                    ${timeIndex % 4 === 0 ? 'border-t border-[#E4E4E7]' : ''}
                  `}
                  onClick={(e) => !isOccupied && !isDisabled && !isDragging && handleCellClickInternal(e, dayIndex, time)}
                  onDragOver={(e) => !isDisabled && handleDragOver(e, dayIndex, time)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => !isDisabled && handleDrop(e, dayIndex, time)}
                  data-testid={`cell-${dayIndex}-${time}`}
                >
                  {isDisabled && (
                    <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(220,38,38,0.08)_4px,rgba(220,38,38,0.08)_8px)]" />
                  )}
                  {lessonAtCell && (() => {
                    const colorIdx = getStudentColorIndex(lessonAtCell.student_id);
                    return (
                      <div
                        draggable="true"
                        onDragStart={(e) => handleDragStart(e, lessonAtCell)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isDragging && !resizingLesson) onEditLesson(lessonAtCell);
                        }}
                        className={`
                          absolute left-1 right-1 top-1 z-10 rounded px-2 py-1
                          cursor-grab active:cursor-grabbing
                          transition-all duration-150
                          hover:shadow-lg hover:-translate-y-0.5
                          ${draggingLesson?.id === lessonAtCell.id ? 'opacity-50 scale-105 shadow-2xl ring-2 ring-blue-500' : ''}
                          ${resizingLesson?.id === lessonAtCell.id ? 'ring-2 ring-green-500' : ''}
                        `}
                        style={{
                          backgroundColor: LESSON_COLORS[colorIdx].bg,
                          color: LESSON_COLORS[colorIdx].text,
                          borderLeft: `3px solid ${LESSON_COLORS[colorIdx].border}`,
                          height: `${getSlotCount(lessonAtCell.start_time, lessonAtCell.end_time) * 28 - 4}px`,
                        }}
                        data-testid={`lesson-${lessonAtCell.id}`}
                      >
                        {lessonAtCell.is_prova && (
                          onToggleProva ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Rimuovere il flag PROVA da questa lezione? Confermare solo se l\'alunno ha deciso di iscriversi definitivamente.')) {
                                  onToggleProva(lessonAtCell.id, false);
                                }
                              }}
                              className="absolute -top-1 -right-1 z-20 px-1 py-0.5 bg-amber-500 hover:bg-amber-600 text-white text-[8px] font-extrabold uppercase tracking-wider rounded shadow"
                              title="Clicca per confermare l'iscrizione (rimuove il flag PROVA)"
                              data-testid={`lesson-prova-badge-${lessonAtCell.id}`}
                            >
                              PROVA
                            </button>
                          ) : (
                            <div className="absolute -top-1 -right-1 z-20 px-1 py-0.5 bg-amber-500 text-white text-[8px] font-extrabold uppercase tracking-wider rounded shadow" title="Lezione di prova" data-testid={`lesson-prova-badge-${lessonAtCell.id}`}>
                              PROVA
                            </div>
                          )
                        )}
                        <div className="flex justify-between items-start h-full">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs truncate uppercase">{lessonAtCell.student_name}</div>
                            <div className="text-[10px] opacity-75 uppercase">{lessonAtCell.start_time} - {lessonAtCell.end_time}</div>
                            {(() => {
                              const active = new Set(lessonAtCell.status_flags || []);
                              const editable = !!onToggleStatusFlag;
                              return (
                                <div className="mt-0.5 flex gap-0.5 -mx-0.5" data-testid={`lesson-status-flags-${lessonAtCell.id}`}>
                                  {STATUS_FLAGS_ORDER.map(f => {
                                    const isOn = active.has(f);
                                    return (
                                      <button
                                        key={f}
                                        type="button"
                                        disabled={!editable}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!editable) return;
                                          onToggleStatusFlag(lessonAtCell.id, f, !isOn);
                                        }}
                                        className={`flex-1 h-4 text-[8px] font-extrabold uppercase tracking-tighter rounded border transition-colors ${isOn ? 'bg-green-500 text-white border-green-600' : 'bg-white/70 text-black/60 border-black/30 hover:bg-white'} ${editable ? 'cursor-pointer' : 'cursor-default'} relative z-20`}
                                        title={`${STATUS_FLAG_LABELS[f]}: ${isOn ? 'attivo' : 'non attivo'}`}
                                        data-testid={`flag-btn-${lessonAtCell.id}-${f}`}
                                      >
                                        {STATUS_FLAG_LABELS[f]}
                                      </button>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                            {(lessonAtCell.duplicated_years?.length > 0 || lessonAtCell.attendance_count > 0) && (
                              <div className="flex gap-1 mt-0.5 flex-wrap" data-testid={`lesson-badges-${lessonAtCell.id}`}>
                                {lessonAtCell.duplicated_years?.length > 0 && (
                                  <span
                                    className="inline-flex items-center gap-0.5 text-[9px] font-bold bg-black/15 px-1 py-0.5 rounded"
                                    title={`Duplicata anno ${lessonAtCell.duplicated_years.map(y => `${y}/${(y+1).toString().slice(-2)}`).join(', ')}`}
                                    data-testid={`lesson-duplicated-${lessonAtCell.id}`}
                                  >
                                    ⇒ {lessonAtCell.duplicated_years.map(y => (y+1).toString().slice(-2)).join(',')}
                                  </span>
                                )}
                                {lessonAtCell.attendance_count > 0 && (
                                  <span
                                    className="inline-flex items-center gap-0.5 text-[9px] font-bold bg-white/50 text-black px-1 py-0.5 rounded"
                                    title={`${lessonAtCell.attendance_count} presenze registrate`}
                                    data-testid={`lesson-attendance-count-${lessonAtCell.id}`}
                                  >
                                    #{lessonAtCell.attendance_count}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          {(onDeleteLesson || onDuplicateLesson || onGenerateAttendance) && <div className="flex flex-col gap-0.5 ml-1 flex-shrink-0">
                            {onGenerateAttendance && <button
                              onClick={(e) => { e.stopPropagation(); onGenerateAttendance(lessonAtCell); }}
                              className="p-0.5 hover:bg-black/10 rounded"
                              title="Crea presenze su intervallo date"
                              data-testid={`generate-attendance-${lessonAtCell.id}`}
                            >
                              <CalendarPlus size={11} />
                            </button>}
                            {onDuplicateLesson && <button
                              onClick={(e) => { e.stopPropagation(); onDuplicateLesson(lessonAtCell.id); }}
                              className="p-0.5 hover:bg-black/10 rounded"
                              title="Duplica nell'anno successivo"
                              data-testid={`duplicate-lesson-${lessonAtCell.id}`}
                            >
                              <Copy size={11} />
                            </button>}
                            {onDeleteLesson && <button
                            onClick={(e) => { e.stopPropagation(); onDeleteLesson(lessonAtCell.id, e); }}
                            className="p-0.5 hover:bg-black/10 rounded"
                            title="Elimina lezione"
                          >
                            <X size={12} />
                          </button>}
                          </div>}
                        </div>
                        <div
                          onMouseDown={(e) => handleResizeStart(e, lessonAtCell)}
                          className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-black/20 rounded-b transition-colors group z-0"
                          title="Trascina per ridimensionare"
                        >
                          <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-8 h-1 bg-current opacity-30 rounded group-hover:opacity-60" />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Duration Picker Popup */}
      {durationPicker && (
        <div
          ref={pickerRef}
          className="fixed z-50 bg-[#2D2A4A] rounded-lg shadow-2xl p-2 min-w-[160px]"
          style={{ left: durationPicker.x - 80, top: durationPicker.y + 4 }}
        >
          <div className="text-[10px] text-white/60 uppercase font-semibold tracking-wider px-2 py-1 mb-1">Durata lezione</div>
          {DURATION_OPTIONS.map(opt => {
            const startIndex = timeToSlotIndex(durationPicker.time);
            const fitsInGrid = startIndex + opt.slots <= TIME_SLOTS.length;
            return (
              <button
                key={opt.slots}
                onClick={() => fitsInGrid && handleDurationSelect(opt.slots)}
                disabled={!fitsInGrid}
                className={`w-full text-left px-4 py-2.5 text-sm font-semibold rounded-md transition-colors mb-1 ${fitsInGrid ? 'text-white hover:bg-white/20 active:bg-white/30' : 'text-white/20 cursor-not-allowed'}`}
                data-testid={`duration-${opt.slots}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});
ScheduleGrid.displayName = 'ScheduleGrid';
